/* A booking that is already over, dropped on a square that already holds a turn.
 *
 * The board cannot answer this one on its own, and that is the whole reason the
 * sheet exists. A completed service landing on a taken square is either:
 *
 *   the same sitting  — a manicure and a pedicure done one after the other for
 *                       one client are two lines in the book, two services the
 *                       technician performed, and ONE turn. The square grows a
 *                       second service and keeps its place.
 *   its own square    — two separate pieces of work, one of which was never
 *                       written down. It takes that position and everything from
 *                       there down moves a square, which on the lower half of a
 *                       half square means the service that was sharing it drops
 *                       into the next one.
 *
 * Nothing distinguishes those from the outside. Both are a card over a square,
 * both are common, and guessing wrong is either a technician credited for one
 * turn when they did two or credited for two when they did one — which is a
 * place in the rotation either way. So it is asked.
 *
 * What a merged square COUNTS for is asked with it, on the same sheet, because
 * that is the only weight on the board no service type can reconstruct. It opens
 * on the heavier of the two, which is right for the ordinary case — a half turn
 * with a full service alongside it was a full turn's work — and wrong often
 * enough that it has to be a control rather than a rule.
 *
 * Nothing here works anything out. `store.dropChoiceDetail` re-reads the board on
 * every paint, the same way the move sheet does: a sheet can sit open across a
 * webhook, an auto-stop or another station's drag, and a sheet describing a
 * board that has moved on is a sheet that gets said yes to.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const DropChoice = {
  name: 'DropChoice',
  setup: function () {
    var d = store.dropChoiceDetail;

    // "4 → 5 turns", so the number that decides who gets the next walk-in reads
    // as a change rather than as a total. Same line the move sheet uses.
    function creditLine(side) {
      return store.formatCredit(side.was) + ' → ' + store.pluralTurns(side.now);
    }

    // What the separate square disturbs, in one sentence. The displaced service
    // is named when there is one: on a half square that is the whole of what is
    // being decided, and "1 square below moves down" does not say it.
    var moves = Vue.computed(function () {
      if (!d.value) return '';
      var s = d.value.separate;
      if (s.displaced) {
        return s.displaced + (s.displacedRunning ? ', still running,' : '') +
          ' drops to the next square';
      }
      if (!s.moved) return 'Nothing below it moves';
      return s.moved + (s.moved === 1 ? ' square below moves down' : ' squares below move down');
    });

    return {
      d: d,
      moves: moves,
      creditLine: creditLine,
      formatCredit: store.formatCredit,
      setWeight: store.setDropChoiceWeight,
      close: store.closeDropChoice,
      confirm: store.confirmDropChoice
    };
  },
  template: [
    '<div v-if="d" class="modal-scrim" @click.self="close">',
    '  <div class="sheet sheet--choice" role="dialog" aria-modal="true">',
    '    <div class="sheet__head" :style="{ \'--svc\': d.color }">',
    '      <span class="sheet__letter">{{ d.letter }}</span>',
    '      <div>',
    '        <h3>Was this the same sitting?</h3>',
    '        <p>{{ d.clientName }}<client-note v-if="d.clientName" :client-id="d.clientId" />{{ d.clientName ? " · " : "" }}{{ d.landing.name }}',
    '          · {{ d.landing.from }} &ndash; {{ d.landing.to }}</p>',
    '      </div>',
    '    </div>',
    '',
    /* The board has moved on underneath the sheet — the day was closed, or
       another station seated the same booking. Said rather than left to be
       discovered by a button that fails when it is pressed. */
    '    <p v-if="d.blocked" class="sheet__blocked">{{ d.blocked }}</p>',
    '',
    /* What is already on the square, each service with its own clock. This is
       the comparison the desk is actually making — did these happen one after
       the other for one person — so it is the first thing on the sheet and it
       is times, not names alone. */
    '    <div class="choice__square">',
    '      <span class="choice__square-label">{{ d.staffName }}&rsquo;s square</span>',
    /* The whole square. On a half square that is two clients, and which of them
       this would join is settled by the side of the slash the card came down
       on — so the one being joined is marked rather than left to be inferred
       from a heading. */
    '      <ul class="choice__svcs">',
    '        <li v-for="s in d.existing" :key="s.key"',
    '          :class="{ \'is-joining\': s.joining }">',
    '          <span class="choice__dot" :style="{ background: s.color }"></span>',
    '          <span class="choice__svc-name">{{ s.client ? s.client + " · " : "" }}{{ s.name }}</span>',
    '          <span v-if="s.joining" class="choice__joining">joining this</span>',
    '          <span class="choice__svc-time">{{ s.from }} &ndash;',
    '            {{ s.running ? "now" : s.to }}</span>',
    '        </li>',
    '      </ul>',
    '    </div>',
    '',
    /* Anything the board already believed that either answer runs across. Not
       conflicts — neither choice is wrong — so they are stated once, above both
       buttons, rather than attached to one of them. */
    '    <ul v-if="d.notes.length" class="sheet__conflicts">',
    '      <li v-for="n in d.notes" :key="n">',
    '        <span class="sheet__conflict-mark" aria-hidden="true">&#9888;</span>',
    '        <span>{{ n }}</span>',
    '      </li>',
    '    </ul>',
    '',
    /* ---- the two answers, each carrying what it would do ---- */
    '    <div class="choice__ways">',
    '',
    '      <section class="choice__way">',
    '        <h4>One sitting</h4>',
    '        <p class="choice__what">It joins the square as a second service.',
    '          Nothing moves and nothing else changes.</p>',
    /* The one control on the sheet. A merged square's weight is the desk's
       answer and the board has no way to work it out, so it is asked here and
       nowhere else — see `applyServiceWeight`. */
    '        <div class="choice__weight">',
    '          <span class="choice__weight-label">Counts as</span>',
    '          <div class="choice__weight-opts" role="radiogroup" aria-label="What the square counts for">',
    '            <button v-for="w in d.merged.choices" :key="w" type="button"',
    '              class="choice__weight-opt" :class="{ \'is-on\': d.merged.weight === w }"',
    '              role="radio" :aria-checked="d.merged.weight === w"',
    '              @click="setWeight(w)">{{ formatCredit(w) }}</button>',
    '          </div>',
    '        </div>',
    '        <p class="choice__credit">{{ d.staffName }}: {{ creditLine(d.merged.credit) }}</p>',
    '        <button type="button" class="btn btn--lg choice__go" :disabled="!!d.blocked"',
    '          @click="confirm(\'merge\')">Merge into the square</button>',
    '      </section>',
    '',
    '      <section class="choice__way">',
    '        <h4>Its own square</h4>',
    '        <p class="choice__what">It takes turn {{ d.separate.square }} and',
    '          {{ moves }}.</p>',
    '        <p class="choice__credit">{{ d.staffName }}: {{ creditLine(d.separate.credit) }}</p>',
    /* Off when there is no turn to start — an unmapped booking can be recorded
       on a square but cannot become one. The reason is already in the notes
       above, so the button only has to be unavailable. */
    '        <button type="button" class="btn btn--lg choice__go"',
    '          :disabled="!!d.blocked || !d.separate.can"',
    '          @click="confirm(\'separate\')">Add as its own square</button>',
    '      </section>',
    '',
    '    </div>',
    '',
    '    <div class="sheet__actions">',
    '      <button type="button" class="btn btn--quiet" @click="close">Cancel</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
